import { useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ProfileViewer from '../profile/ProfileViewer';

const PREVIEW_WINDOW = {
  MIN_WIDTH: 400, MAX_WIDTH: 1200,
  MIN_HEIGHT: 250, MAX_HEIGHT: 600,
  MIN_RESIZE_WIDTH: 200, MIN_RESIZE_HEIGHT: 120,
  REF_LENGTH: 100,
  ASPECT_PADDING: 1.3,
  EDGE_MARGIN: 10,
};

const PREVIEW_DRAG_INITIAL = {
  dragging: false, resizeDir: null,
  startX: 0, startY: 0, startLeft: 0, startTop: 0, startWidth: 360, startHeight: 220,
};

/**
 * Detect resize direction and cursor from mouse position relative to a rect.
 */
function getResizeEdge(clientX, clientY, rect) {
  const x = clientX - rect.left;
  const y = clientY - rect.top;
  const nearLeft = x <= PREVIEW_WINDOW.EDGE_MARGIN;
  const nearRight = x >= rect.width - PREVIEW_WINDOW.EDGE_MARGIN;
  const nearTop = y <= PREVIEW_WINDOW.EDGE_MARGIN;
  const nearBottom = y >= rect.height - PREVIEW_WINDOW.EDGE_MARGIN;

  let resizeDir = null;
  if (nearLeft && nearTop) resizeDir = 'nw';
  else if (nearRight && nearTop) resizeDir = 'ne';
  else if (nearLeft && nearBottom) resizeDir = 'sw';
  else if (nearRight && nearBottom) resizeDir = 'se';
  else if (nearLeft) resizeDir = 'w';
  else if (nearRight) resizeDir = 'e';
  else if (nearTop) resizeDir = 'n';
  else if (nearBottom) resizeDir = 's';

  let cursor = 'default';
  if ((nearLeft && nearTop) || (nearRight && nearBottom)) cursor = 'nwse-resize';
  else if ((nearRight && nearTop) || (nearLeft && nearBottom)) cursor = 'nesw-resize';
  else if (nearLeft || nearRight) cursor = 'ew-resize';
  else if (nearTop || nearBottom) cursor = 'ns-resize';

  return { resizeDir, cursor };
}

/**
 * Draggable and resizable floating window that shows a cross-section profile.
 * Props:
 *   open               {boolean}  - Whether the window is visible
 *   profile            {Object}   - Profile model object from ProfilerTools
 *   token              {string}
 *   tilesetId          {string|number}
 *   onClose            {Function} - Called when the X button is clicked
 *   onSetProfileLoadingId {Function} - Called with profileId while loading, null when ready
 *   t                  {Function} - i18next translation function
 */
export function FloatingProfilePreview({ open, profile, token, tilesetId, onClose, onSetProfileLoadingId, t }) {
  const dragRef = useRef({ ...PREVIEW_DRAG_INITIAL });
  const autoSizeRef = useRef(true);
  const [state, setState] = useState({
    visible: false, left: 0, top: 0, width: 800, height: 400, cursor: 'default',
  });

  // Initialize position/size from profile dimensions whenever a new profile opens
  useEffect(() => {
    if (!open) return;
    autoSizeRef.current = true;
    const { MIN_WIDTH, MAX_WIDTH, MIN_HEIGHT, MAX_HEIGHT, REF_LENGTH } = PREVIEW_WINDOW;
    const dims = profile?.model?.dimensions;
    const profileLength = (dims && dims.y > 0) ? dims.y : REF_LENGTH;
    const w = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(MIN_WIDTH * (profileLength / REF_LENGTH))));
    const h = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, 350));
    setState({
      visible: false,
      left: Math.max(8, window.innerWidth - w - 12),
      top: Math.max(8, window.innerHeight - h - 44),
      width: w,
      height: h,
      cursor: 'default',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  if (!open) return null;

  const rect = { left: state.left, top: state.top, width: state.width, height: state.height };

  const handleMouseMove = (e) => {
    if (dragRef.current.dragging || dragRef.current.resizeDir) return;
    const { cursor } = getResizeEdge(e.clientX, e.clientY, rect);
    setState(prev => ({ ...prev, cursor }));
  };

  const handleMouseDown = (e) => {
    const { resizeDir } = getResizeEdge(e.clientX, e.clientY, rect);
    const y = e.clientY - rect.top;
    dragRef.current = {
      dragging: !resizeDir && y <= 32,
      resizeDir,
      startX: e.clientX,
      startY: e.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      startWidth: rect.width,
      startHeight: rect.height,
    };

    const onMove = (ev) => {
      const dr = dragRef.current;
      if (!dr.dragging && !dr.resizeDir) return;
      let left = dr.startLeft;
      let top = dr.startTop;
      let width = dr.startWidth;
      let height = dr.startHeight;
      const dx = ev.clientX - dr.startX;
      const dy = ev.clientY - dr.startY;

      if (dr.dragging) {
        left = Math.max(0, dr.startLeft + dx);
        top = Math.max(0, dr.startTop + dy);
      }
      if (dr.resizeDir) {
        autoSizeRef.current = false;
        const { MIN_RESIZE_WIDTH: minW, MIN_RESIZE_HEIGHT: minH } = PREVIEW_WINDOW;
        if (dr.resizeDir.includes('e')) width = Math.max(minW, dr.startWidth + dx);
        if (dr.resizeDir.includes('s')) height = Math.max(minH, dr.startHeight + dy);
        if (dr.resizeDir.includes('w')) {
          left = Math.min(dr.startLeft + dx, dr.startLeft + dr.startWidth - minW);
          width = Math.max(minW, dr.startWidth - dx);
        }
        if (dr.resizeDir.includes('n')) {
          top = Math.min(dr.startTop + dy, dr.startTop + dr.startHeight - minH);
          height = Math.max(minH, dr.startHeight - dy);
        }
      }
      setState(prev => ({ ...prev, left, top, width, height }));
    };

    const onUp = () => {
      dragRef.current = { ...PREVIEW_DRAG_INITIAL };
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      setState(prev => ({ ...prev, cursor: 'default' }));
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        left: state.left,
        top: state.top,
        width: state.width,
        height: state.height,
        bgcolor: 'rgba(20,20,30,0.6)',
        borderRadius: '12px',
        zIndex: 1500,
        boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
        border: '1px solid rgba(255,255,255,0.08)',
        backdropFilter: 'blur(16px)',
        opacity: state.visible ? 1 : 0,
        transition: 'opacity 0.15s ease-in',
        pointerEvents: state.visible ? 'auto' : 'none',
        color: '#fff',
        cursor: state.cursor,
        userSelect: 'none',
        overflow: 'hidden'
      }}
      onMouseMove={handleMouseMove}
      onMouseDown={handleMouseDown}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 24, pl: 2, pr: 1, pt: 1 }}>
        <Typography variant="caption" sx={{ color: '#fff' }}>
          {t('ProfileView') || 'Profile View'}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: '#fff', p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: '14px' }} />
        </IconButton>
      </Box>
      <Box sx={{ width: '100%', height: 'calc(100% - 34px)' }}>
        <ProfileViewer
          token={token}
          tilesetId={tilesetId}
          profile={profile}
          onReady={() => {
            onSetProfileLoadingId(null);
            setState(prev => ({ ...prev, visible: true }));
          }}
          onDataRange={({ length: profLen, elevationSpan }) => {
            if (!profLen || !elevationSpan || elevationSpan <= 0) return;
            setState(prev => {
              const updates = { ...prev, visible: true };
              if (autoSizeRef.current) {
                const { MIN_WIDTH, MAX_WIDTH, MIN_HEIGHT, MAX_HEIGHT, ASPECT_PADDING } = PREVIEW_WINDOW;
                const aspect = (profLen / elevationSpan) * ASPECT_PADDING;
                const area = prev.width * prev.height;
                let w = Math.round(Math.sqrt(area * aspect));
                let h = Math.round(area / w);
                w = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, w));
                h = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, h));
                updates.width = w;
                updates.height = h;
                updates.left = Math.max(8, window.innerWidth - w - 12);
                updates.top = Math.max(8, window.innerHeight - h - 44);
              }
              return updates;
            });
          }}
        />
      </Box>
    </Box>
  );
}

export default FloatingProfilePreview;
